import React, { useRef } from 'react';
import { Controller, Control, FieldError, RegisterOptions, get, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@material-ui/core';
import { Autocomplete, AutocompleteInputChangeReason } from '@material-ui/lab';

interface ComboboxProps {
  name: string;
  label?: string;
  control: Control<any>;
  options: OptionsCombobox[];
  error?: FieldError | undefined;
  rule?: RegisterOptions;
  placeholder?: string;
  onInputChange?: (value: string) => void;
  onOptionSelect?: (item: OptionsCombobox) => void;
  onScrollBottom?: () => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  maxLength?: number;
  loading?: boolean;
  inputReadOnly?: boolean;
}

type OptionsCombobox = {
  label: string;
  value: number | string | null;
  [key: string]: unknown;
};

const Combobox: React.FC<ComboboxProps> = ({
  name,
  control,
  options,
  error,
  placeholder,
  onInputChange,
  onOptionSelect,
  onScrollBottom,
  disabled,
  size = 'small',
  rule,
  maxLength,
  loading,
  inputReadOnly,
}: ComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const prevValueRef = useRef('');
  const listboxRef = useRef<HTMLUListElement | null>(null);

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget as HTMLUListElement;
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10) {
      onScrollBottom?.();
    }
  };

  const handleInputChange = (
    _: React.ChangeEvent<unknown>,
    newInputValue: string,
    reason?: AutocompleteInputChangeReason
  ): void => {
    if (reason === 'input') {
      setInputValue(newInputValue);
      onInputChange?.(newInputValue);
    }

    if (reason === 'clear') {
      onInputChange?.('');
      setInputValue('');
      onOptionSelect?.({ label: '', value: null });
      setOpen(true);
    }
  };

  const value = useWatch({ control, name });

  useEffect(() => {
    if (prevValueRef.current === value || !options.length) return;

    prevValueRef.current = value;

    const selectedLabel = options.find((opt) => opt.value === value)?.label || '';

    if (selectedLabel) setInputValue(selectedLabel);
  }, [options, value]);

  return (
    <Box>
      <Controller
        name={name}
        control={control}
        rules={rule}
        render={({
          field,
        }: {
          field: { value: any; onChange: (value: any) => void; onBlur: () => void; name: string };
        }) => (
          <Autocomplete
            {...field}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            noOptionsText={loading ? '読み込み中…' : 'データなし'}
            id={name}
            size={size}
            options={options}
            ListboxProps={{
              onScroll: handleScroll,
              ref: listboxRef,
            }}
            getOptionLabel={(option: OptionsCombobox | string) => {
              if (typeof option === 'string') return option;
              return option?.label ?? '';
            }}
            value={options.find((opt: OptionsCombobox) => opt.value === field.value) ?? null}
            onChange={(_, value) => {
              let selectedValue: string | number | null = '';

              if (value && typeof value === 'object') {
                selectedValue = value.value;
                setInputValue(value.label);
                onOptionSelect?.(value);
              } else {
                setInputValue('');
              }

              field.onChange(selectedValue);
            }}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            renderOption={(option: OptionsCombobox | string, state: { selected: boolean }) => {
              // Only handle OptionsCombobox type
              if (typeof option === 'string') {
                return <span>{option}</span>;
              }
              return (
                <Box
                  key={option.value}
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '2px',
                  }}
                >
                  <Typography
                    style={{
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      color: state.selected ? '#1976d2' : 'inherit',
                    }}
                  >
                    {option.label}
                  </Typography>
                </Box>
              );
            }}
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                placeholder={placeholder}
                error={!!error}
                disabled={disabled}
                helperText={error?.message}
                inputProps={{
                  ...params.inputProps,
                  maxLength: maxLength,
                  readOnly: inputReadOnly,
                }}
              />
            )}
          />
        )}
      />
    </Box>
  );
};

export default Combobox;
